import { memo, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import useForm from 'antd/lib/form/hooks/useForm';

import MessageWrapper from 'components/MessageWrapper/MessageWrapper';
import WithSettingsForm from 'components/Settings/hocs/WithSettingsForm';
import MyStoreCustomizableBranding from 'components/Settings/MyStoreTab/MyStoreCustomizableBranding';
import MyStoreGeneral from 'components/Settings/MyStoreTab/MyStoreGeneral';
import MyStoreLogo from 'components/Settings/MyStoreTab/MyStoreLogo';
import MyStoreSync from 'components/Settings/MyStoreTab/MyStoreSync';
import SettingsSkeleton from 'components/Skeletons/Settings/SettingsSkeleton';
import { categoriesSelector } from 'store/selectors/categoriesSelectors';
import { selectStoreSettings } from 'store/selectors/settingsSelectors';
import { getCategoriesAction } from 'store/actions/categoriesActions';
import { getSettingsByKeyAction, updateSettingsByKeyAction } from 'store/actions/settingsActions';
import { ESettingsKey } from 'store/reducers/settingsReducer/settingsTypes';
import { useTypedSelector } from 'hooks/useTypedSelector';
import sendGTM from 'utils/sendGTM';
import { SettingsTabPaths, syncProductsWarningText } from 'components/Settings/constants';
import { GTM_UPDATE_SETTINGS } from 'utils/constants/gtmConstants';
import { EStoreFields } from 'types/settings/enums';
import { ISelectedCategory } from 'types/settings/interfaces';
import { ErrorIcn } from 'utils/icons';

const MyStoreTab = () => {
  const dispatch = useDispatch();
  const { isLoading, isUpdated, ...storeSettings } = useTypedSelector(selectStoreSettings);
  const { isUpdated: isUpdatedCategories } = useTypedSelector(categoriesSelector);
  const [form] = useForm();
  const { category, categoryName, url, name } = storeSettings;
  const [selectedCategory, setSelectedCategory] = useState<ISelectedCategory>({ category, categoryName });

  const handleSubmit = async() => {
    const values = form.getFieldsValue();

    await dispatch(updateSettingsByKeyAction({ ...values, ...selectedCategory }, ESettingsKey.StoreSettings));

    if (form.isFieldTouched(EStoreFields.CategoryName)) {
      sendGTM({
        ...GTM_UPDATE_SETTINGS,
        ...(form.isFieldTouched(EStoreFields.CategoryName)
          && { supplier_store_category: selectedCategory[EStoreFields.CategoryName] }),
      });
    }
  };

  useEffect(() => {
    !isUpdated && dispatch(getSettingsByKeyAction(ESettingsKey.StoreSettings));
    !isUpdatedCategories && dispatch(getCategoriesAction());
  }, []);

  return (
    isLoading
      ? <SettingsSkeleton />
      : <>
        <WithSettingsForm
          form={form}
          initialValues={storeSettings}
          rootPath={SettingsTabPaths.MyStore}
          onSubmit={handleSubmit}
        >
          <MessageWrapper
            isVisible={!!url && !name}
            className="mb-5 text-black-70 bg-black-5 dark:text-black-30 dark:bg-black-90"
          >
            <ErrorIcn className="text-black-70 mr-1 dark:text-black-30" />
            {syncProductsWarningText}
          </MessageWrapper>
          <MyStoreGeneral setSelectedCategory={setSelectedCategory} />
          <MyStoreLogo {...form} />
          <MyStoreCustomizableBranding />
          <MyStoreSync />
        </WithSettingsForm>
      </>
  );
};

export default memo(MyStoreTab);
